import { useEffect, useMemo, useRef } from 'react'

/**
 * Trailing-edge debounce hook. Returns a stable function that defers the
 * latest call by `delay` ms; a newer call before the timer fires cancels the
 * previous one. Exposes `.cancel()` to abort a pending call (e.g. on unmount).
 *
 * The callback reference inside the hook is captured via a ref so the
 * debounced function always sees the freshest closure without being
 * re-created on every render.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number,
): ((...args: Args) => void) & { cancel: () => void } {
  const fnRef = useRef(fn)
  fnRef.current = fn

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debounced = useMemo(() => {
    const invoke = (...args: Args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        fnRef.current(...args)
      }, delay)
    }

    const withCancel = invoke as ((...args: Args) => void) & { cancel: () => void }
    withCancel.cancel = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    return withCancel
  }, [delay])

  useEffect(() => {
    return () => debounced.cancel()
  }, [debounced])

  return debounced
}
